import React, { useState, useRef, useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { iRootState } from "../../store";
import styled, { css } from "styled-components";
import qs from "query-string";
import { withI18n } from "react-i18next";
import Loading from "../Common/Loading";
import HeaderComponent from "../HeaderComponent";
import CWBLabsHeader from "../CWBLabs/components/Header";
import { HEADER_HEIGHT } from "../../constants";
// @ts-ignore
import { toast } from "cwb-react";

interface Props extends StateProps, DispatchProps {
  location?: any;
  history?: any;
  t?: any;
}

type QueryObjType = {
  tk?: string,
  cid?: string,
  onboarding: string;
  ac?: string,
  signUp?: number,
}

const OnboardingComponent: React.FC<Props> = ({
  authenticatedUser,
  sessionData,
  user,
  onboardingLoading,
  onboardingErrors,
  uploadPhoto,
  uploadResumeWithRoster,
  resetUpload,
  loading,
  t,
}) => {
  const params = qs.parse(window.location.search);
  const showHeader = !params.hs;
  const isCWBLabs = false;
  const agentId = params.ag as string;

  const redirect = useCallback(() => {
    let queryObj: QueryObjType = {
      onboarding: 'true'
    }

    if (params?.signUp) {
      queryObj.signUp = Number(params.signUp);
    }

    if (params?.ac && params?.ac === 'true') {
      queryObj.ac = params.ac as string;
      queryObj.signUp = 1
    }


    if (sessionData && sessionData.WebSessionKey)
      queryObj.tk = sessionData.WebSessionKey;
    if (authenticatedUser && authenticatedUser.id)
      queryObj.cid = authenticatedUser.id.userId;

    const queryString = qs.stringify(queryObj);
    const url = `${process.env.REACT_APP_CWB_Site}/actorsworkbook/default.asp?${queryString}`
    window.top.location.href = url;
    ;
  }, [authenticatedUser, sessionData, params?.ac, params?.signUp]);

  const [image1, setImage1] = useState<File | null>(null);
  const [image2, setImage2] = useState<File | null>(null);
  const [resume, setResume] = useState<File | null>(null);
  const [dragActive1, setDragActive1] = useState(false);
  const [dragActive2, setDragActive2] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [dragActiveResume, setDragActiveResume] = useState(false);

  // Clear error message after timeout
  useEffect(() => {
    if (errorMessage) {
      const timeout = setTimeout(() => {
        setErrorMessage('');
      }, 5000); // 3 second timeout
      return () => clearTimeout(timeout);
    }
  }, [errorMessage]);

  // Clear Redux errors when component mounts or when files change
  useEffect(() => {
    if (onboardingErrors.uploadError) {
      resetUpload();
    }
  }, [resetUpload, onboardingErrors.uploadError]);

  const fileInput1 = useRef<HTMLInputElement>(null);
  const fileInput2 = useRef<HTMLInputElement>(null);
  const resumeInput = useRef<HTMLInputElement>(null);

  const validateFile = (file: File, isResume: boolean = false): string | null => {
    const MAX_PHOTO_SIZE = 5 * 1024 * 1024; // 5MB
    const MAX_RESUME_SIZE = 5 * 1024 * 1024; // 5MB for PDF
    
    if (isResume) {
      // Validate PDF file type
      if (file.type !== 'application/pdf') {
        return t('Only PDF files are accepted for resumes');
      }
      
      // Validate PDF file size
      if (file.size > MAX_RESUME_SIZE) {
        return t('Resume file must be smaller than 5MB');
      }
    } else {
      // Validate specific image file types: JPG/JPEG, PNG, GIF
      const allowedImageTypes = [
        'image/jpeg',
        'image/jpg', 
        'image/png',
        'image/gif'
      ];
      
      if (!allowedImageTypes.includes(file.type.toLowerCase())) {
        return t('Only JPG/JPEG, PNG, and GIF images are allowed');
      }
      
      // Validate image file size
      if (file.size > MAX_PHOTO_SIZE) {
        return t('Image file must be smaller than 5MB');
      }
    }
    
    return null; // Valid file
  };


  const handleDrag = (e: React.DragEvent, setDragActive: (active: boolean) => void) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent, setFile: (file: File) => void, setDragActive: (active: boolean) => void, acceptedTypes: string[], isResume: boolean = false) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      
      // Validate file
      const validationError = validateFile(file, isResume);
      if (validationError) {
        setErrorMessage(validationError);
        return;
      }

      
      if (acceptedTypes.some(type => file.type.startsWith(type) || file.name.toLowerCase().endsWith(type.replace('*', '')))) {
        setFile(file);
        // Clear any previous error messages when file is successfully selected
        setErrorMessage('');
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, setFile: (file: File) => void, isResume: boolean = false) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      const validationError = validateFile(file, isResume);
      if (validationError) {
        setErrorMessage(validationError);
        e.target.value = ''; // Clear the input
        return;
      }

      
      setFile(file);
      // Clear any previous error messages when file is successfully selected
      setErrorMessage('');
    }
  };

  const handleContinue = async () => {
    if (authenticatedUser?.id?.userId) {
      try {
        // Clear any previous error messages
        setErrorMessage('');
        resetUpload();
        
        // Perform additional client-side validation before upload
        if (image1) {
          const validationError1 = validateFile(image1, false);
          if (validationError1) {
            setErrorMessage(validationError1);
            return;
          }
        }
        
        if (image2) {
          const validationError2 = validateFile(image2, false);
          if (validationError2) {
            setErrorMessage(validationError2);
            return;
          }
        }
        
        if (resume) {
          const validationError3 = validateFile(resume, true);
          if (validationError3) {
            setErrorMessage(validationError3);
            return;
          }
        }

        // Upload files - errors will be thrown and caught by outer try-catch
        if (image1) {
          await uploadPhoto({
            clientId: authenticatedUser.id.userId,
            file: image1,
          });
        }
        
        if (image2) {
          await uploadPhoto({
            clientId: authenticatedUser.id.userId,
            file: image2,
          });
        }
        
        if (resume) {
          await uploadResumeWithRoster({
            clientId: authenticatedUser.id.userId,
            file: resume,
            agentId: agentId,
            title: t('Resume'),
          });
        }
        
        // If we reach here, all uploads were successful
        // Clear any error messages that might still be showing
        setErrorMessage('');
        resetUpload();
        
        const hasPhotos = image1 || image2;
        const hasResume = resume;
        let successMessage;
        
        if (hasPhotos && hasResume) {
          // successMessage = t('Files uploaded successfully!');
        } else if (hasPhotos && !hasResume) {
          // successMessage = t('Photos uploaded successfully!');
        } else if (!hasPhotos && hasResume) {
          // successMessage = t('Resume uploaded successfully!');
        }
        
        if (successMessage) {
          // toast.success(successMessage, {autoClose: 3000, closeOnClick: true, pauseOnFocusLoss: false});
        }
        // Only redirect if uploads were successful
        redirect();
      } catch (error) {
        console.error('Upload failed:', error);
        // Don't redirect on upload failure - show specific error message
        const uploadErrorMessage = error instanceof Error ? error.message : t('Upload failed. Please try again.');
        setErrorMessage(uploadErrorMessage);
        return;
      }
    } else {
      redirect();
    }
  };

  const handleSkip = () => {
    if (authenticatedUser) {
      redirect();
    }
  };

  // Delete handlers
  const handleDeleteImage1 = (e: React.MouseEvent) => {
    e.stopPropagation();
    setImage1(null);
  };

  const handleDeleteImage2 = (e: React.MouseEvent) => {
    e.stopPropagation();
    setImage2(null);
  };

  const handleDeleteResume = (e: React.MouseEvent) => {
    e.stopPropagation();
    setResume(null);
  };

  // Rechose handlers
  const handleRechooseImage1 = (e: React.MouseEvent) => {
    e.stopPropagation();
    fileInput1.current?.click();
  };

  const handleRechooseImage2 = (e: React.MouseEvent) => {
    e.stopPropagation();
    fileInput2.current?.click();
  };

  const handleRechooseResume = (e: React.MouseEvent) => {
    e.stopPropagation();
    resumeInput.current?.click();
  };

  const isUploadDisabled = !image1 && !image2 && !resume;

  if(loading){
    return <Loading show={true} />;
  }
  
  return (
    <>
      {showHeader && (isCWBLabs ? (<CWBLabsHeader />) : (
        <HeaderComponent />
      ))}
      <Wrapper showHeader={showHeader}>
        <ContentContainer>
          <Title>{t('Add two images and your first resume')}</Title>
          <Subtitle>
            {t('These will be shared with your agent so they can immediately submit you for jobs!')}
          </Subtitle>

          <ImagesContainer>
            <DropzoneContainer 
              isDragActive={dragActive1}
              onDragEnter={(e) => handleDrag(e, setDragActive1)}
              onDragLeave={(e) => handleDrag(e, setDragActive1)}
              onDragOver={(e) => handleDrag(e, setDragActive1)}
              onDrop={(e) => handleDrop(e, setImage1, setDragActive1, ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'], false)}
              onClick={() => fileInput1.current?.click()}
            >
              <input 
                ref={fileInput1}
                type="file" 
                accept="image/jpeg,image/jpg,image/png,image/gif"
                onChange={(e) => handleFileSelect(e, setImage1, false)}
                style={{ display: 'none' }}
              />
              {image1 ? (
                <UploadedImage>
                  <img src={URL.createObjectURL(image1)} alt="Uploaded" />
                  {/* <FileName>{image1.name}</FileName> */}
                  <ImageActionsContainer>
                    <DeleteIcon onClick={handleDeleteImage1}>
                      <img src="../images/close.png" alt="Delete" />
                    </DeleteIcon>
                    <RefreshIcon onClick={handleRechooseImage1}>
                      <img src="../images/refresh.png" alt="Refresh" />
                    </RefreshIcon>
                  </ImageActionsContainer>
                </UploadedImage>
              ) : (
                <DropzoneContent>
                  <img alt="plus icon" src="../images/plus-icon.svg" />
                  <DropzoneText>
                    {t('Drop here or click')}
                    <br />
                    {t('to upload')}
                  </DropzoneText>
                </DropzoneContent>
              )}
            </DropzoneContainer>

            <DropzoneContainer 
              isDragActive={dragActive2}
              onDragEnter={(e) => handleDrag(e, setDragActive2)}
              onDragLeave={(e) => handleDrag(e, setDragActive2)}
              onDragOver={(e) => handleDrag(e, setDragActive2)}
              onDrop={(e) => handleDrop(e, setImage2, setDragActive2, ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'], false)}
              onClick={() => fileInput2.current?.click()}
            >
              <input 
                ref={fileInput2}
                type="file" 
                accept="image/jpeg,image/jpg,image/png,image/gif"
                onChange={(e) => handleFileSelect(e, setImage2, false)}
                style={{ display: 'none' }}
              />
              {image2 ? (
                <UploadedImage>
                  <img src={URL.createObjectURL(image2)} alt="Uploaded" />
                  {/* <FileName>{image2.name}</FileName> */}
                  <ImageActionsContainer>
                    <DeleteIcon onClick={handleDeleteImage2}>
                      <img src="../images/close.png" alt="Delete" />
                    </DeleteIcon>
                    <RefreshIcon onClick={handleRechooseImage2}>
                      <img src="../images/refresh.png" alt="Refresh" />
                    </RefreshIcon>
                  </ImageActionsContainer>
                </UploadedImage>
              ) : (
                <DropzoneContent>
                  <img alt="plus icon" src="../images/plus-icon.svg" />
                  <DropzoneText>
                    {t('Drop here or click')}
                    <br />
                    {t('to upload')}
                  </DropzoneText>
                </DropzoneContent>
              )}
            </DropzoneContainer>
          </ImagesContainer>
          <ResumeContainer 
            isDragActive={dragActiveResume}
            onDragEnter={(e) => handleDrag(e, setDragActiveResume)}
            onDragLeave={(e) => handleDrag(e, setDragActiveResume)}
            onDragOver={(e) => handleDrag(e, setDragActiveResume)}
            onDrop={(e) => handleDrop(e, setResume, setDragActiveResume, ['application/pdf', '.pdf'], true)}
            onClick={() => resumeInput.current?.click()}
          >
            <input 
              ref={resumeInput}
              type="file" 
              accept=".pdf"
              onChange={(e) => handleFileSelect(e, setResume, true)}
              style={{ display: 'none' }}
            />
            {resume ? (
              <ResumeUploaded>
                <ResumeNameContainer>
                  <ResumeText>{resume.name}</ResumeText>
                  <SuccessIcon>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" fill="#4CAF50"/>
                      <path d="m9 12 2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </SuccessIcon>
                </ResumeNameContainer>
                <ResumeIcons>
                  <ResumeRefreshIcon onClick={handleRechooseResume}>
                    <img src="../images/refresh.png" alt="Refresh" />
                  </ResumeRefreshIcon>
                  <ResumeDeleteIcon onClick={handleDeleteResume}>
                    <img src="../images/close.png" alt="Delete" />
                  </ResumeDeleteIcon>
                </ResumeIcons>
              </ResumeUploaded>
            ) : (
              <ResumeContent>
                <img alt="plus icon" src="../images/plus-icon.svg" />
                <ResumeText>{t('Add resume (pdf)')}</ResumeText>
              </ResumeContent>
            )}
          </ResumeContainer>


          {/* <ErrorMessageContainer> */}
            {(onboardingErrors.uploadError || errorMessage) && (
              <ErrorMessage>{onboardingErrors.uploadError || errorMessage}</ErrorMessage>
            )}
          {/* </ErrorMessageContainer> */}

          <ButtonsContainer>
            <SkipButton onClick={handleSkip}>{t('Skip')}</SkipButton>
            <ContinueButton 
              onClick={handleContinue} 
              disabled={isUploadDisabled || onboardingLoading}
            >
              {onboardingLoading ? t('Uploading...') : t('Continue')}
            </ContinueButton>
          </ButtonsContainer>
        </ContentContainer>
      </Wrapper>
    </>
  );
};

const Wrapper: any = styled.div`
  display: flex;
  padding-top: ${HEADER_HEIGHT}px;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  width: 100%;
  background-color: #f8f9fa;

  ${(props: any) =>
    !props.showHeader &&
    css`
      padding-top: 0;
    `}

  @media all and (max-width: 520px) {
    flex-direction: column;
  }

  @media all and (max-width: 768px) {
    padding-top: 0;
  }
`;

const ContentContainer = styled.div`
  padding: 0px;
  max-width: 600px;
  width: 100%;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 20px;
  color: #333;
  margin-bottom: 10px;
  font-weight: 600;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 35px;
  line-height: 1.5;
`;

const ImagesContainer = styled.div`
  display: flex;
  gap: 18px;
  margin-bottom: 15px;
  justify-content: center;

  @media all and (max-width: 520px) {
    flex-direction: row;
    align-items: center;
  }
`;

const DropzoneContainer = styled.div<{ isDragActive: boolean }>`
  width: 200px;
  height: 250px;
  border: 0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: #fff;
  transition: all 0.2s ease;
  overflow: hidden;

  &:hover {
    border-color: #007bff;
    background-color: #f8f9ff;
  }
`;

const DropzoneContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  img{
    margin-bottom: 8px;
  }
`;

const PlusIcon = styled.div`
  width: 40px;
  height: 40px;
  border: 2px solid #ccc;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #ccc;
  margin-bottom: 12px;
`;

const DropzoneText = styled.p`
  font-size: 14px;
  color: #666;
  text-align: center;
  line-height: 1.4;
`;

const UploadedImage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  width: 100%;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }
`;

const FileName = styled.p`
  position: absolute;
  bottom: 8px;
  left: 8px;
  right: 8px;
  font-size: 12px;
  color: #fff;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  padding: 16px 8px 8px;
  margin: 0;
  border-radius: 0 0 16px 16px;
`;

const ResumeContainer = styled.div<{ isDragActive: boolean }>`
  width: 100%;
  max-width: 418px;
  height: 52px;
  border: 0px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #fff;
  transition: all 0.2s ease;
  margin: 0 auto 60px;
  padding: 20px;

  &:hover {
    border-color: #007bff;
    background-color: #f8f9ff;
  }
`;

const ResumeContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  img{
    width: 25px;
  }
`;

const ResumeUploaded = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  position: relative;
  width: 100%;
`;

const ResumeNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ResumeText = styled.span`
  font-size: 16px;
  color: #666;
`;

const ErrorMessageContainer = styled.div`
  min-height: 52px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  background-color: #ffebee;
  border: 1px solid #e57373;
  border-radius: 4px;
  padding: 12px;
  font-size: 14px;
  text-align: center;
  width: 100%;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
`;

const SkipButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 16px;
  cursor: pointer;
  padding: 6px 24px;
  
  &:hover {
    color: #333;
  }
`;

const ContinueButton = styled.button`
  background-color: #2ED2FF;
  color: white;
  border: none;
  padding: 10px 48px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s;
  width: 100%;
  max-width: 418px;

  &:hover {
    background-color: #00acc1;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ImageActionsContainer = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 10;
`;

const RefreshIcon = styled.div`
  width: 32px;
  height: 32px;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 1);
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  img {
    width: 20px;
    height: 20px;
    opacity: 1;
    object-fit: contain;
  }
  
  &:hover img {
    opacity: 1;
  }
`;

const DeleteIcon = styled.div`
  width: 32px;
  height: 32px;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 1);
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  img {
    width: 16px;
    height: auto;
    opacity: 1;
    object-fit: contain;
  }
  
  &:hover img {
    opacity: 1;
  }
`;

const ResumeIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SuccessIcon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ResumeRefreshIcon = styled.div`
  width: 32px;
  height: 32px;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.075);
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 1);
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  img {
    width: 20px;
    height: 20px;
    opacity: 1;
    object-fit: contain;
  }
  
  &:hover img {
    opacity: 1;
  }
`;

const ResumeDeleteIcon = styled.div`
  width: 32px;
  height: 32px;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.075);
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 1);
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  img {
    width: 16px;
    height: auto;
    opacity: 1;
    object-fit: contain;
  }
  
  &:hover img {
    opacity: 1;
  }
`;

interface StateProps {
  authenticatedUser: any;
  sessionData: any;
  user: any;
  onboardingLoading: boolean;
  onboardingErrors: any;
  loading: any;
}

interface DispatchProps {
  uploadPhoto: (payload: { clientId: string; file: File }) => Promise<any>;
  uploadResumeWithRoster: (payload: { clientId: string; file: File; agentId?: string; title?: string }) => Promise<any>;
  resetUpload: () => void;
}

const mapStateToProps = (state: iRootState): StateProps => ({
  authenticatedUser: state.appModel.authenticatedUser,
  user: state.authModel.user,
  sessionData: state.appModel.sessionData,
  onboardingLoading: state.onboardingModel.isLoading,
  onboardingErrors: state.onboardingModel.errors,
  loading: state.authModel.createCurrentBridgeLoading || state.authModel.createSeamlessApiLoading,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  uploadPhoto: dispatch.onboardingModel.uploadPhoto,
  uploadResumeWithRoster: dispatch.onboardingModel.uploadResumeWithRoster,
  resetUpload: dispatch.onboardingModel.resetUpload,
});

export default withI18n()(
  connect(mapStateToProps, mapDispatchToProps)(OnboardingComponent)
);